import { CONTENT_TYPES } from 'constants/games/contentTypes'

import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUser } from './user/useUser'

const INIT_STATE = {
  currentGame: CONTENT_TYPES.LANDING,
  isOpenLeftPanel: false,
  isOpenRightPanel: false,
  hideRightPanel: true,
  hideLeftPanel: false,
}

const layoutV3StateAtom = atom(INIT_STATE)
const setPanelAtom = atom(null, (get, set, { panel, isOpen }) => {
  set(layoutV3StateAtom, (prev) => ({
    ...prev,
    isOpenLeftPanel: panel === 'left' ? isOpen : prev.isOpenLeftPanel,
    isOpenRightPanel: panel === 'right' ? isOpen : prev.isOpenRightPanel,
  }))
})
const setCurrentGameAtom = atom(null, (get, set, { gameType, preferences }) => {
  const { isOpenRightPanel } = get(layoutV3StateAtom)
  set(layoutV3StateAtom, (prev) => {
    switch (gameType) {
      case CONTENT_TYPES.G_3MIN:
        return {
          currentGame: gameType,
          isOpenLeftPanel: false,
          isOpenRightPanel: false,
          hideRightPanel: true,
        }
      case CONTENT_TYPES.G_24HR:
        return {
          currentGame: gameType,
          isOpenLeftPanel: false,
          isOpenRightPanel: false,
          hideRightPanel: true,
          hideLeftPanel: false,
        }
      case CONTENT_TYPES.LVG_CRYPTO:
      case CONTENT_TYPES.LVG_STOCKS:
      case CONTENT_TYPES.LVG_INDEXES:
      case CONTENT_TYPES.LVG_AI_INDEXES:
        return {
          currentGame: gameType,
          isOpenLeftPanel: true,
          isOpenRightPanel: preferences?.socialFeedPanel || isOpenRightPanel,
          hideRightPanel: false,
          hideLeftPanel: false,
        }
      case CONTENT_TYPES.G_BUSTED:
        return {
          currentGame: gameType,
          isOpenLeftPanel: true,
          isOpenRightPanel: true,
          hideRightPanel: false,
          hideLeftPanel: false,
        }
      case CONTENT_TYPES.WALLET:
      case CONTENT_TYPES.RESTRICTED:
        return {
          currentGame: gameType,
          isOpenLeftPanel: false,
          isOpenRightPanel: false,
          hideRightPanel: false,
          hideLeftPanel: false,
        }
      case CONTENT_TYPES.REFERRALS:
        return {
          currentGame: gameType,
          isOpenLeftPanel: false,
          isOpenRightPanel: false,
          hideRightPanel: false,
          hideLeftPanel: false,
        }
      case CONTENT_TYPES.YOLO_TOKEN:
      case CONTENT_TYPES.PRIVATE_PRESALE:
      case CONTENT_TYPES.PUBLIC_PRESALE:
        return {
          currentGame: gameType,
          isOpenLeftPanel: false,
          isOpenRightPanel: false,
          hideRightPanel: true,
          hideLeftPanel: true,
        }
      case CONTENT_TYPES.ACHIEVEMENT:
        return {
          currentGame: gameType,
          isOpenLeftPanel: false,
          isOpenRightPanel: true,
          hideRightPanel: false,
          hideLeftPanel: false,
        }
      default:
        return {
          currentGame: CONTENT_TYPES.LANDING,
          isOpenLeftPanel: false,
          isOpenRightPanel: false,
          hideRightPanel: true,
          hideLeftPanel: false,
        }
    }
  })
})

export const useLayoutV3State = () => {
  const preferences = useUser('preferences')
  const navigateTo = useNavigate()
  const location = useLocation()
  const state = useAtomValue(layoutV3StateAtom)
  const setPanel = useSetAtom(setPanelAtom)
  const setActiveGame = useSetAtom(setCurrentGameAtom)

  const setOpenRightPanel = useCallback((isOpen) => setPanel({ panel: 'right', isOpen }), [setPanel])
  const setOpenLeftPanel = useCallback((isOpen) => setPanel({ panel: 'left', isOpen }), [setPanel])
  const setCurrentGame = useCallback(
    (gameType, applayPreferences) => {
      setActiveGame({ gameType, preferences: applayPreferences ? preferences : {} })
      if (gameType === CONTENT_TYPES.LANDING) navigateTo('/game')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setActiveGame, location, preferences]
  )

  return {
    ...state,
    setOpenLeftPanel,
    setOpenRightPanel,
    setCurrentGame,
  }
}
